import { createHttpClient } from "./http";
import store from "@/store";

const api = createHttpClient("/api/admin", { timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = store.state.admin.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response && err.response.status === 401) {
      if (store.state.admin.authenticated) {
        const msg = err.response.data?.message || "";
        store.dispatch("admin/logout", msg);
      }
    }
    if (err.response && err.response.status === 403) {
      const msg = err.response.data?.message || "权限不足";
      console.warn("[admin] 403 Forbidden:", msg);
    }
    return Promise.reject(err);
  },
);

/* ---- Auth ---- */
export function loginAdmin(username, password, captchaToken, captchaCode) {
  return api.post("/login", { username, password, captchaToken, captchaCode });
}
export function verifyTotp(tempToken, totpCode) {
  return api.post("/login/verify-totp", { tempToken, totpCode });
}
export function verifyEmailCode(tempToken, emailCode) {
  return api.post("/login/verify-email", { tempToken, emailCode });
}
export function resendEmailCode(tempToken) {
  return api.post("/login/resend-email", { tempToken });
}
export function changePasswordLogin(tempToken, newPassword) {
  return api.post("/login/change-password", { tempToken, newPassword });
}
export function fetchCaptcha() {
  return api.get("/captcha");
}
export function fetchCaptchaNeeded() {
  return api.get("/captcha-needed");
}

/* ---- News ---- */
export function fetchAdminNews(params) {
  return api.get("/news", { params });
}
export function createNews(data) {
  return api.post("/news", data);
}
export function updateNews(id, data) {
  return api.put(`/news/${id}`, data);
}
export function deleteNews(id) {
  return api.delete(`/news/${id}`);
}
export function deleteNewsBatch(ids) {
  return api.post("/news/batch-delete", { ids });
}
export function reorderNews(orders) {
  return api.put("/news/reorder", { orders });
}
export function fetchNewsCategories() {
  return api.get("/news/categories");
}

/* ---- Projects ---- */
export function fetchAdminProjects(params) {
  return api.get("/projects", { params });
}
export function createProject(data) {
  return api.post("/projects", data);
}
export function updateProject(id, data) {
  return api.put(`/projects/${id}`, data);
}
export function deleteProject(id) {
  return api.delete(`/projects/${id}`);
}
export function deleteProjectBatch(ids) {
  return api.post("/projects/batch-delete", { ids });
}
export function reorderProjects(orders) {
  return api.put("/projects/reorder", { orders });
}
export function fetchProjectCategories() {
  return api.get("/projects/categories");
}

/* ---- Jobs ---- */
export function fetchAdminJobs(params) {
  return api.get("/jobs", { params });
}
export function createJob(data) {
  return api.post("/jobs", data);
}
export function updateJob(id, data) {
  return api.put(`/jobs/${id}`, data);
}
export function deleteJob(id) {
  return api.delete(`/jobs/${id}`);
}
export function deleteJobBatch(ids) {
  return api.post("/jobs/batch-delete", { ids });
}
export function reorderJobs(orders) {
  return api.put("/jobs/reorder", { orders });
}

/* ---- Upload ---- */
export function uploadImage(file, onUploadProgress) {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload", fd, {
    timeout: 60000,
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
}

export function uploadVideo(file, onUploadProgress) {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload", fd, {
    timeout: 300000,
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
}

export function uploadHlsFiles(files, onUploadProgress) {
  const fd = new FormData();
  files.forEach((file) => fd.append("files", file));
  return api.post("/upload/hls", fd, {
    timeout: 600000,
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
}

/* ---- Company ---- */
export function fetchAdminCompany() {
  return api.get("/company");
}
export function updateCompany(data) {
  return api.put("/company", data);
}

/* ---- Messages ---- */
export function fetchMessages(params) {
  return api.get("/messages", { params });
}

/* ---- Visitor stats ---- */
export function fetchVisitorStats() {
  return api.get("/visitor-stats");
}
export function updateMessage(id, data) {
  return api.put(`/messages/${id}`, data);
}
export function deleteMessage(id) {
  return api.delete(`/messages/${id}`);
}
export function deleteMessageBatch(ids) {
  return api.post("/messages/batch-delete", { ids });
}

/* ---- Users ---- */
export function fetchAdminUsers() {
  return api.get("/users");
}
export function fetchAdminUser(id) {
  return api.get(`/users/${id}`);
}
export function createUser(data) {
  return api.post("/users", data);
}
export function updateUser(id, data) {
  return api.put(`/users/${id}`, data);
}
export function deleteUser(id) {
  return api.delete(`/users/${id}`);
}
export function resetUserPassword(id, data) {
  return api.put(`/users/${id}/reset-password`, data);
}
export function toggleUserLock(id) {
  return api.put(`/users/${id}/toggle-lock`);
}
export function kickUser(id) {
  return api.post(`/users/${id}/kick`);
}

/* ---- TOTP (2FA) ---- */
export function setupTotp(userId) {
  return api.post(`/users/${userId}/totp-setup`);
}
export function verifyTotpSetup(userId, code) {
  return api.post(`/users/${userId}/totp-verify`, { code });
}
export function disableTotp(userId) {
  return api.post(`/users/${userId}/totp-disable`);
}

/* ---- Mobile Login ---- */
export function enableMobileLogin(userId) {
  return api.post(`/users/${userId}/mobile-login-enable`);
}
export function disableMobileLogin(userId) {
  return api.post(`/users/${userId}/mobile-login-disable`);
}

/* ---- Email Verification ---- */
export function enableEmailVerification(userId) {
  return api.post(`/users/${userId}/email-verify-enable`);
}
export function disableEmailVerification(userId) {
  return api.post(`/users/${userId}/email-verify-disable`);
}

/* ---- Audit Logs ---- */
export function fetchAuditLogs(params) {
  return api.get("/audit-logs", { params });
}

/* ---- Roles ---- */
export function fetchRoles() {
  return api.get("/roles");
}
export function fetchRole(id) {
  return api.get(`/roles/${id}`);
}
export function fetchRoleOptions() {
  return api.get("/roles/options");
}
export function fetchPermissionGroups() {
  return api.get("/roles/permissions");
}
export function createRole(data) {
  return api.post("/roles", data);
}
export function updateRole(id, data) {
  return api.put(`/roles/${id}`, data);
}
export function deleteRole(id) {
  return api.delete(`/roles/${id}`);
}

/* ---- Menus ---- */
export function fetchMenus() {
  return api.get("/menus");
}
export function fetchMyMenus() {
  return api.get("/my-menus");
}
export function createMenu(data) {
  return api.post("/menus", data);
}
export function updateMenu(id, data) {
  return api.put(`/menus/${id}`, data);
}
export function reorderMenus(data) {
  return api.put("/menus/reorder", data);
}
export function deleteMenu(id) {
  return api.delete(`/menus/${id}`);
}

/* ---- Visitors ---- */
export function fetchVisitors(params) {
  return api.get("/visitors", { params });
}
export function lookupIp(ip, force = false) {
  const params = { ip };
  if (force) params.force = "true";
  return api.get("/visitors/ip-lookup", { params });
}

/* ---- Violations / Content Check ---- */
export function fetchSensitiveWords() {
  return api.get("/violations/words");
}
export function updateSensitiveWords(words) {
  return api.put("/violations/words", { words });
}
export function checkContentText(text) {
  return api.post("/violations/check", { text });
}
export function scanContent(type) {
  return api.post("/violations/scan", { type });
}
export function fetchViolationConfig() {
  return api.get("/violations/config");
}
